/**
 * usePushNotifications — client hook to enable/disable Firebase Cloud Messaging
 * push on the current device.
 *
 * Everything runs in the browser (event handlers / effects), so the Firebase
 * SDK is dynamically imported and never touches SSR. The messaging service
 * worker receives the publishable config via query string.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/lib/auth/auth-client";
import { firebaseConfig, vapidKey, isPushConfigured } from "./firebase-config";

export type PushStatus = "unsupported" | "default" | "granted" | "denied";

const SW_URL = "/firebase-messaging-sw.js";

function detectSupport(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "Notification" in window &&
    "PushManager" in window
  );
}

function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function devicePlatform(): string {
  if (isIos()) return "ios";
  if (typeof navigator !== "undefined" && /Android/.test(navigator.userAgent)) {
    return "android";
  }
  return "web";
}

function swRegistrationUrl(): string {
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey,
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
  });
  return `${SW_URL}?${params.toString()}`;
}

export function usePushNotifications() {
  const { user } = useSession();
  const [supported, setSupported] = useState(false);
  const [status, setStatus] = useState<PushStatus>("default");
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(false);

  // iOS only delivers web push to *installed* PWAs (iOS 16.4+).
  const iosNeedsInstall = isIos() && !isStandalone();

  useEffect(() => {
    const ok = detectSupport();
    setSupported(ok);
    if (ok) {
      setStatus(Notification.permission as PushStatus);
      setEnabled(Notification.permission === "granted");
    } else {
      setStatus("unsupported");
    }
  }, []);

  const enable = useCallback(async (): Promise<{ ok: boolean; reason?: string }> => {
    if (!isPushConfigured) return { ok: false, reason: "not_configured" };
    if (!supported) return { ok: false, reason: "unsupported" };
    if (!user?.id) return { ok: false, reason: "not_signed_in" };
    if (iosNeedsInstall) return { ok: false, reason: "ios_needs_install" };

    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      setStatus(permission as PushStatus);
      if (permission !== "granted") {
        return { ok: false, reason: "permission_denied" };
      }

      const registration = await navigator.serviceWorker.register(swRegistrationUrl());
      await navigator.serviceWorker.ready;

      const { initializeApp, getApps } = await import("firebase/app");
      const { getMessaging, getToken } = await import("firebase/messaging");
      const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
      const messaging = getMessaging(app);

      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
      if (!token) return { ok: false, reason: "no_token" };

      const { error } = await supabase.from("push_device").upsert(
        {
          user_id: user.id,
          fcm_token: token,
          user_agent: navigator.userAgent.slice(0, 400),
          platform: devicePlatform(),
          last_seen_at: new Date().toISOString(),
        },
        { onConflict: "fcm_token" },
      );
      if (error) return { ok: false, reason: error.message };

      setEnabled(true);
      return { ok: true };
    } catch (err) {
      console.error("Enable push failed:", err);
      return { ok: false, reason: "error" };
    } finally {
      setBusy(false);
    }
  }, [supported, user?.id, iosNeedsInstall]);

  const disable = useCallback(async (): Promise<{ ok: boolean }> => {
    setBusy(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration(SW_URL);
      if (registration) {
        try {
          const { initializeApp, getApps } = await import("firebase/app");
          const { getMessaging, getToken, deleteToken } = await import(
            "firebase/messaging"
          );
          const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
          const messaging = getMessaging(app);
          const token = await getToken(messaging, {
            vapidKey,
            serviceWorkerRegistration: registration,
          }).catch(() => null);
          if (token) {
            await supabase.from("push_device").delete().eq("fcm_token", token);
            await deleteToken(messaging).catch(() => {});
          }
        } catch (e) {
          console.warn("Token cleanup skipped:", e);
        }
      }
      setEnabled(false);
      return { ok: true };
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    /** Whether the browser supports web push at all. */
    supported,
    /** Whether a Firebase project has been wired up. */
    configured: isPushConfigured,
    /** Current Notification permission state. */
    status,
    /** True while a request is in flight. */
    busy,
    /** True when push is active on this device. */
    enabled,
    /** iOS device that must "Add to Home Screen" before push works. */
    iosNeedsInstall,
    enable,
    disable,
  };
}
